import React, { createContext, useContext, useState, useEffect } from 'react';
import { CheckCircle, Download, X, AlertCircle, PanelRightOpen } from 'lucide-react';

// Toast Context
const ToastContext = createContext();

// Toast Provider Component
export const ToastProvider = ({ children }) => {
    const [toasts, setToasts] = useState([]);

    const addToast = (message, type = 'success', duration = 3000, action = null) => {
        const id = Date.now() + Math.random();
        const newToast = { id, message, type, duration, action };

        setToasts(prev => [...prev, newToast]);

        // Auto remove toast after duration
        setTimeout(() => {
            removeToast(id);
        }, duration);
    };

    const removeToast = (id) => {
        setToasts(prev => prev.filter(toast => toast.id !== id));
    };


    const showCopyToast = (paletteName, format) => {
        const formatLabels = {
            css: 'CSS Variables',
            scss: 'SCSS Map',
            js: 'JavaScript',
            array: 'Array',
            hexArray: 'Array',
            rgb: 'RGB Values',
            hsl: 'HSL Values',
            hex: 'HEX Values',
            svg: 'SVG Code',
            json: 'JSON',
            tailwind: 'Tailwind Colors',
            palette: 'Palette Colors'
        };

        // Check if this is a color info item copy (new format)
        // If format looks like a color value (starts with # or contains spaces/parentheses), treat differently
        if (format && (format.startsWith('#') || format.includes('(') || format.includes(' ') || format.includes('rgb') || format.includes('hsl') || format.includes('%') || format.includes(','))) {
            // This is a color info item copy: paletteName is actually the label, format is the value
            addToast(`Copied "${paletteName}"`, 'copy');
        } else {
            // This is a regular palette copy
            const formatLabel = formatLabels[format] || format.toUpperCase();
            const name = paletteName || 'palette';
            addToast(`Copied "${name}" as ${formatLabel}`, 'copy');
        }
    };

    const showDownloadToast = (paletteName, format) => {
        const formatLabels = {
            svg: 'SVG file',
            ase: 'ASE file',
            sketch: 'Sketch palette',
            png: 'PNG image',
            pdf: 'PDF file',
            txt: 'text file'
        };

        const formatLabel = formatLabels[format] || `${format.toUpperCase()} file`;
        const name = paletteName || 'palette';
        addToast(`Downloaded "${name}" as ${formatLabel}`, 'download');
    };

    // New function specifically for visualizer actions
    const showVisualizerToast = (paletteName) => {
        const name = paletteName || 'Palette';
        addToast(`Added "${name}" to Visualizer`, 'visualizer');
    };

    return (
        <ToastContext.Provider value={{ addToast, showCopyToast, showDownloadToast, showVisualizerToast }}>
            {children}
            <ToastContainer toasts={toasts} removeToast={removeToast} />
        </ToastContext.Provider>
    );
};

// Hook to use toast
export const useToast = () => {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error('useToast must be used within a ToastProvider');
    }
    return context;
};

// Toast Container Component
const ToastContainer = ({ toasts, removeToast }) => {
    if (toasts.length === 0) return null;

    return (
        <div className="fixed top-4 right-4 z-50 space-y-2">
            {toasts.map(toast => (
                <Toast key={toast.id} toast={toast} onRemove={removeToast} />
            ))}
        </div>
    );
};

// Individual Toast Component
const Toast = ({ toast, onRemove }) => {
    const [isVisible, setIsVisible] = useState(false);
    const [isLeaving, setIsLeaving] = useState(false);

    useEffect(() => {
        // Trigger entrance animation
        const timer = setTimeout(() => setIsVisible(true), 10);
        return () => clearTimeout(timer);
    }, []);

    const handleRemove = () => {
        setIsLeaving(true);
        setTimeout(() => onRemove(toast.id), 150);
    };

    const getIcon = () => {
        switch (toast.type) {
            case 'copy':
                return <CheckCircle className="w-5 h-5 text-green-500" />;
            case 'download':
                return <Download className="w-5 h-5 text-blue-500" />;
            case 'visualizer':
                return <PanelRightOpen className="w-5 h-5 text-purple-500" />;
            case 'error':
                return <AlertCircle className="w-5 h-5 text-red-500" />;
            default:
                return <CheckCircle className="w-5 h-5 text-green-500" />;
        }
    };

    return (
        <div
            className={`
                transform transition-all duration-200 ease-out
                ${isVisible && !isLeaving
                    ? 'translate-x-0 opacity-100'
                    : 'translate-x-full opacity-0'}
                bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700
                min-w-[300px] max-w-[400px] pointer-events-auto
            `}
        >
            <div className="p-4">
                <div className="flex items-center gap-3">
                    {getIcon()}
                    <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {toast.message}
                        </p>
                    </div>
                    <button
                        onClick={handleRemove}
                        className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                    >
                        <X className="w-4 h-4 text-gray-400" />
                    </button>
                </div>
                {toast.action && (
                    <div className="mt-2 ml-8">
                        <button
                            onClick={() => {
                                toast.action.onClick();
                                handleRemove();
                            }}
                            className="text-xs font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
                        >
                            {toast.action.label}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};